import { Chip } from '@stern-ui/ui';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
	saveBatchRowSelection,
	savePageNumber,
	savePerformingInlineTableAction,
	selectBatchRowSelection,
	selectPageNumber,
	selectTabValue,
} from '@/components';
import { getCurrentTabValue } from '@/utils';

import { useRemoveNewMutation } from '../../api';
import { CellContextExtended, PersonListObject } from '../../types';

type ListParams = {
	list_identifier: string;
};

// This component is used to display a "New" chip for patients that are marked as new in a patient list.
// List ID is taken from the URL params and patient ID is taken from the row data.

export const ListPatientsNewChip = ({
	info,
	canUpdate,
}: {
	info: CellContextExtended;
	canUpdate: boolean;
}) => {
	const { list_identifier } = useParams<ListParams>();
	const { id } = info.row.original as PersonListObject;
	const dispatch = useDispatch();

	const pageNumber = useSelector(selectPageNumber);
	const tabValue = useSelector(selectTabValue);
	const batchRowSelection = useSelector(selectBatchRowSelection);
	// tab index 1 is the "New to list" tab, see ListTabsContainer
	const isNewTab = getCurrentTabValue(0, tabValue) === 1;

	const [isRemoving, setIsRemoving] = useState(false);

	const [removeNew, { isError }] = useRemoveNewMutation();

	const doRemoveNew = (listId: string, patientId: string) => {
		setIsRemoving(true);
		// set the performing inline table action state to true to prevent the table from flickering to the loading state while the refetch is in progress
		dispatch(savePerformingInlineTableAction({ isPerformingInlineTableAction: true }));
		const isOnlyRowOnPage = info.table.getRowModel().rows.length === 1;
		if (isNewTab) {
			// on the New tab, if this is the only row on the current page, removing it means this page
			// will no longer exist once refetched, so navigate back to what will be the new last page
			if (isOnlyRowOnPage && (pageNumber ?? 1) > 1) {
				dispatch(savePageNumber({ pageNumber: (pageNumber ?? 1) - 1 }));
			}
			// if this patient exists in the batch selection, remove it from the selection as it will no longer exist in the list
			if (batchRowSelection && batchRowSelection[patientId]) {
				const newBatchSelection = { ...batchRowSelection };
				delete newBatchSelection[patientId];
				dispatch(saveBatchRowSelection({ batchRowSelection: newBatchSelection }));
			}
		}
		removeNew({ list_identifier: listId, patientIds: [patientId] });
	};

	useEffect(() => {
		// reset the isRemoving state after completion, if isSuccess then this component will be unmounted so no need to reset
		if (isError) {
			setIsRemoving(false);
		}
	}, [isError]);

	return (
		<Chip
			size='sm'
			style='new'
			value='New'
			className='inline-flex items-center justify-between'
			buttonClassName={`${isRemoving ? '' : 'hover:text-success'} ml-1`}
			dataTestId='list-patient-new-chip'
			onClose={canUpdate ? () => doRemoveNew(list_identifier ?? '', id) : undefined}
			closeDisabled={isRemoving || !canUpdate}
			isLoading={isRemoving}
		/>
	);
};
